/* Benachrichtigungen: Liste, Lesestatus je Meldung, Push-Anmeldung.
 *
 * Der Lesestatus wird pro Meldung geführt, nicht global. Er liegt im Browser
 * des jeweiligen Geräts — wer die App auf Handy und Rechner nutzt, hat also
 * zwei getrennte Stände. Geräteübergreifend ginge nur über eine eigene Tabelle
 * in der Datenbank; das steht als Aufgabe in TODO.md.
 *
 * Übernahme aus der alten Fassung: Dort gab es nur einen Zeitstempel
 * ("alles bis hierher gesehen"). Der wird als Ausgangslinie weiterverwendet —
 * alles, was davor liegt, gilt als gelesen. Dadurch springt beim Umstieg nicht
 * plötzlich alles auf ungelesen.
 */

import { ref, computed } from 'vue';
import * as api from '../supabase/notifications.js';
import { loadReadState, saveReadIds, saveBaseline } from '../domain/notificationReadState.js';

const MAX_ITEMS = 30;
/* Die Liste soll nicht unbegrenzt wachsen; älteste Einträge fallen heraus. */
const MAX_REMEMBERED = 200;

const items = ref([]);
/* Lesestatus gehört dem angemeldeten Nutzer (Schlüssel enthalten die Nutzer-ID,
   siehe domain/notificationReadState.js). Weil diese Refs Singletons sind und ein
   Kontowechsel die Seite nicht neu lädt, werden sie in start() je Nutzer neu
   gefüllt und in stop() geleert. */
const readIds = ref(new Set());
const baseline = ref(0);
const pushAvailable = ref(false);
const pushActive = ref(false);
let channel = null;
let started = false;
let currentUserId = null;
/* Zählt Start/Stop-Wechsel; ein noch laufendes start() eines früheren Nutzers
   erkennt daran, dass es überholt wurde, und schreibt nichts mehr. */
let generation = 0;

function persistReadIds(ids) {
  if (!currentUserId) return;
  saveReadIds(localStorage, currentUserId, [...ids].slice(-MAX_REMEMBERED));
}

export function useNotifications() {
  function isRead(n) {
    if (readIds.value.has(n.id)) return true;
    /* Ausgangslinie aus der alten Fassung */
    return baseline.value > 0 && new Date(n.created_at).getTime() <= baseline.value;
  }

  /* Nur die ungelesenen — die Glocke zeigt ausschließlich diese an. Eine
     gelesene Meldung verschwindet damit sofort aus der Liste, statt nur
     optisch (blasser, ohne Punkt) markiert zu bleiben. Entscheidung Bastian,
     siehe TODO.md „Meldungen: Gelesen-Umfang und Verschwinden nachbessern" —
     sonst stapeln sich alte, bereits gelesene Meldungen in der Glocke. */
  const unreadItems = computed(() => items.value.filter((n) => !isRead(n)));
  const unreadCount = computed(() => unreadItems.value.length);

  function markRead(id) {
    const next = new Set(readIds.value);
    next.add(id);
    readIds.value = next;
    persistReadIds(next);
  }

  function markUnread(id) {
    const next = new Set(readIds.value);
    next.delete(id);
    readIds.value = next;
    persistReadIds(next);

    /* Liegt die Meldung vor der Ausgangslinie, würde sie sofort wieder als
       gelesen gelten. Deshalb die Linie hinter diese Meldung zurückziehen. */
    const item = items.value.find((n) => n.id === id);
    if (item && baseline.value > 0) {
      const ts = new Date(item.created_at).getTime();
      if (ts <= baseline.value) {
        baseline.value = ts - 1;
        if (currentUserId) saveBaseline(localStorage, currentUserId, baseline.value);
      }
    }
  }

  function toggleRead(n) {
    isRead(n) ? markUnread(n.id) : markRead(n.id);
  }

  /* Alle aktuell geladenen Meldungen auf einmal als gelesen markieren — für
     "beim Schließen der Glocke gilt: gesehen, also gelesen" (siehe
     NotificationBell.vue). Eine Kopie des Sets statt N einzelner
     markRead()-Aufrufe, damit nicht N Reaktivitäts-Updates hintereinander
     laufen. */
  function markAllRead() {
    if (!items.value.length) return;
    const next = new Set(readIds.value);
    items.value.forEach((n) => next.add(n.id));
    readIds.value = next;
    persistReadIds(next);
  }

  /** Meldungen zu einem gelöschten Spieler aus der Glocke nehmen (die Datenbank löscht sie getrennt). */
  function dropForPlayer(playerId) {
    items.value = items.value.filter((n) => String(n.player_id ?? '') !== String(playerId));
  }

  async function refreshPushState() {
    pushAvailable.value = api.pushSupported();
    pushActive.value = pushAvailable.value ? await api.isPushSubscribed() : false;
  }

  /* userId: Konto, dessen Lesestatus gilt. Läuft schon ein Start für ein
     anderes Konto, wird erst sauber gestoppt. */
  async function start(userId) {
    if (!userId) return;
    if (started && currentUserId === userId) return;
    if (started) stop();

    started = true;
    currentUserId = userId;
    const gen = ++generation;

    const state = loadReadState(localStorage, userId);
    readIds.value = state.readIds;
    baseline.value = state.baseline;

    const loaded = await api.loadNotifications();
    if (gen !== generation) return;
    items.value = loaded;
    await refreshPushState();
    if (gen !== generation) return;

    channel = api.subscribeNotifications((row) => {
      if (gen !== generation) return;
      items.value.unshift(row);
      if (items.value.length > MAX_ITEMS) items.value.pop();
    });
  }

  function stop() {
    generation++;
    api.unsubscribeNotifications(channel);
    channel = null;
    started = false;
    currentUserId = null;
    items.value = [];
    readIds.value = new Set();
    baseline.value = 0;
  }

  /** Liefert null bei Erfolg, sonst einen i18n-Schlüssel für die Meldung. */
  async function enablePush(userId) {
    const result = await api.enablePush(userId);
    await refreshPushState();
    return result.ok ? null : result.reason;
  }

  return {
    items, unreadItems, unreadCount, isRead,
    markRead, markUnread, toggleRead, markAllRead,
    pushAvailable, pushActive,
    start, stop, enablePush, dropForPlayer,
  };
}
